import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { PageTitle } from '@/components/PageTitle';
import { orpc } from '@/lib/orpc';

export const Route = createFileRoute('/_app/')({
  component: Overview
});

function Overview() {
  const { t } = useTranslation();
  return (
    <div className="space-y-6 p-6">
      <PageTitle title={t('overview.title')} />
      <SystemStatusCard />
    </div>
  );
}

function SystemStatusCard() {
  const { t } = useTranslation();
  const health = useQuery({ ...orpc.system.health.queryOptions(), refetchInterval: 30_000 });

  return (
    <div className="card bg-base-100 max-w-xl shadow-sm">
      <div className="card-body">
        <div className="flex items-center justify-between gap-3">
          <h2 className="card-title text-base">{t('overview.systemStatus')}</h2>
          {health.data && (
            <span className={`badge badge-soft ${health.data.status === 'ok' ? 'badge-success' : 'badge-warning'}`}>
              {health.data.status === 'ok' ? t('overview.statusOk') : t('overview.statusDegraded')}
            </span>
          )}
        </div>

        {health.isPending && <div className="skeleton mt-4 h-20 w-full" />}

        {health.isError && (
          <div role="alert" className="alert alert-error alert-soft mt-4">
            <span className="iconify lucide--plug-zap size-5" />
            <span>{t('errors.NETWORK')}</span>
            <button type="button" className="btn btn-sm" onClick={() => void health.refetch()}>
              {t('common.retry')}
            </button>
          </div>
        )}

        {health.data && (
          <ul className="mt-4 space-y-2 text-sm">
            {(['database', 'redis'] as const).map((key) => {
              const check = health.data.checks[key];
              return (
                <li key={key} className="flex items-center justify-between gap-3">
                  <span className="flex items-center gap-2">
                    <span
                      className={`iconify size-4 ${check.ok ? 'lucide--circle-check text-success' : 'lucide--circle-x text-error'}`}
                    />
                    {t(`overview.${key}`)}
                  </span>
                  <span className="text-base-content/60">
                    {check.ok ? t('overview.latency', { ms: check.latencyMs }) : t('overview.unreachable')}
                  </span>
                </li>
              );
            })}
            <li className="border-base-300 flex items-center justify-between gap-3 border-t border-dashed pt-2">
              <span>{t('overview.version')}</span>
              <span className="text-base-content/60 font-mono text-xs">{health.data.version}</span>
            </li>
          </ul>
        )}
      </div>
    </div>
  );
}
